import { Component, input, OnInit, output, ViewChild } from '@angular/core';

import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { SemantriaUser } from 'app/providers/semantria-api/semantria-users.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { noWhitespaceValidator, passwordsMatchValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { PasswordRulesCheckComponent } from '../password-rules-check/password-rules-check.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-account-password',
  templateUrl: './account-password.component.html',
  styleUrls: ['./account-password.component.scss'],
  standalone: false,
})
export class AccountPasswordDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly user = input<SemantriaUser | undefined>();
  readonly modalClosed = output<string | void>();

  public isLoading = false;
  public requireCurrentPassword = true;
  public allRulesValid = true;
  public accountPasswordStatuses = {
    existingPw: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
    pw1: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
    pw2: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
  };
  isFormValid = false;
  passwordForm: UntypedFormGroup;
  @ViewChild(PasswordRulesCheckComponent)
  passwordRulesCheckComponent: PasswordRulesCheckComponent;

  constructor(
    private alertService: AlertService,
    private authService: AuthService,
    private accountService: SemantriaAccount,
    private fb: UntypedFormBuilder,
    private translocoService: TranslocoService
  ) {
    const validPasswordValidator = (): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        if (!this.passwordRulesCheckComponent) {
          return null;
        }
        return this.passwordRulesCheckComponent.isValid(control.value) ? null : { valid: true };
      };
    };

    this.passwordForm = this.fb.group(
      {
        existingPassword: [''],
        password: [
          '',
          {
            validators: [Validators.required, validPasswordValidator()],
          },
        ],
        passwordConfirm: ['', { validators: [Validators.required] }],
      },
      { validators: passwordsMatchValidator }
    );
  }

  ngOnInit() {
    this.requireCurrentPassword = !this.user();
    if (this.requireCurrentPassword) {
      this.existingPassword.setValidators([Validators.required, noWhitespaceValidator]);
    }
  }

  get existingPassword() {
    return this.passwordForm.controls.existingPassword;
  }

  get password() {
    return this.passwordForm.controls.password;
  }

  get passwordConfirm() {
    return this.passwordForm.controls.passwordConfirm;
  }

  public setIsFormValid(): void {
    this.isFormValid =
      this.password.valid &&
      this.password?.value !== this.existingPassword?.value &&
      this.passwordConfirm.valid &&
      this.allRulesValid;
  }

  public validateExistingPassword(): void {
    if (
      this.existingPassword.hasError('required') ||
      this.existingPassword.hasError('whitespace')
    ) {
      ErrorObjectUtils.setError(
        this.accountPasswordStatuses.existingPw,
        this.translocoService.translate('account-password-component.must-enter-current-password')
      );
    } else {
      ErrorObjectUtils.setNoError(this.accountPasswordStatuses.existingPw);
    }
  }

  public validateNewPassword(control: string): void {
    const inputControl = control === 'pw1' ? this.password : this.passwordConfirm;
    const status =
      control === 'pw1' ? this.accountPasswordStatuses.pw1 : this.accountPasswordStatuses.pw2;

    if (inputControl.hasError('valid')) {
      ErrorObjectUtils.setError(
        status,
        this.translocoService.translate('account-password-component.password-complexity')
      );
    } else if (inputControl.hasError('required')) {
      ErrorObjectUtils.setError(
        status,
        this.translocoService.translate('account-password-component.required-password')
      );
    } else if (this.password?.value === this.existingPassword?.value) {
      ErrorObjectUtils.setError(
        status,
        this.translocoService.translate('account-password-component.new-password-cannot-match')
      );
    } else if (inputControl.hasError('passwordsNotMatch')) {
      ErrorObjectUtils.setError(
        this.accountPasswordStatuses.pw2,
        this.translocoService.translate('account-password-component.non-matched-passwords')
      );
    } else {
      ErrorObjectUtils.setSuccess(status);
    }
  }

  onInputChange(inputName: string) {
    switch (inputName) {
      case 'existingPw':
        this.validateExistingPassword();
        break;
      case 'pw1':
        this.validateNewPassword('pw1');
        break;
      case 'pw2':
        this.validateNewPassword('pw2');
        break;
    }
    this.setIsFormValid();
  }

  public close(): void {
    this.passwordForm.reset();
    this.modalClosed.emit();
  }

  private async changeOwnPassword(): Promise<boolean> {
    const savedPassword = this.password.value;
    const existingPasswword = this.existingPassword.value;
    try {
      const session = await this.accountService.updatePassword(existingPasswword, savedPassword);
      this.authService.setToken(session.info2.token);
      this.alertService.showToast(
        this.translocoService.translate('account-password-component.successfully-changed')
      );
      return true;
    } catch (error) {
      if (error.error.type === 'ChangePasswordError') {
        this.existingPassword.setErrors({ invalid: true });
        this.accountPasswordStatuses.existingPw = {
          ...ErrorObjectUtils.setError(
            this.accountPasswordStatuses.existingPw,
            this.translocoService.translate(
              'account-password-component.must-enter-current-password'
            )
          ),
        };
        this.setIsFormValid();
        return false;
      }
      this.alertService.showErrorToast(
        this.translocoService.translate('account-password-component.failed-to-update'),
        error
      );
      return false;
    }
  }

  private async changeUserPassword(): Promise<boolean> {
    try {
      await this.accountService.changeUserPassword(this.user().user_id, this.password.value);
      this.alertService.showToast(
        this.translocoService.translate(
          'account-password-component.user-password-successfully-changed'
        )
      );
      return true;
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('account-password-component.failed-to-reset'),
        error
      );
      return false;
    }
  }

  public async confirm() {
    this.isLoading = true;

    let ok;
    if (!this.user()) {
      ok = await this.changeOwnPassword();
    } else {
      ok = await this.changeUserPassword();
    }
    this.isLoading = false;

    if (!ok) {
      //don't clear/close the modal on error, but we need to stop the loading state
      return;
    }
    this.passwordForm.reset();
    this.modalClosed.emit();
  }
}
